import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLock } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../config';
import './Admin.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [recoverySecret, setRecoverySecret] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    recoverySecret: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    const errors = {
      recoverySecret: '',
      newPassword: '',
      confirmPassword: ''
    };
    let hasErrors = false;

    if (!recoverySecret.trim()) {
      errors.recoverySecret = 'Recovery secret code is required';
      hasErrors = true;
    }

    if (!newPassword.trim()) {
      errors.newPassword = 'New password is required';
      hasErrors = true;
    } else if (newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters long';
      hasErrors = true;
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your password';
      hasErrors = true;
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      hasErrors = true;
    }

    setFieldErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({ recoverySecret: '', newPassword: '', confirmPassword: '' });

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/reset-password-by-secret`, {
        recoverySecret: recoverySecret.trim(),
        newPassword: newPassword.trim()
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true // Include credentials for CORS
      });

      if (response.data && response.data.success) {
        // Success - show success message and redirect
        setSuccess(true);
        setError('');
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/admin');
        }, 2000);
      } else {
        const errorMessage = response.data?.error || 'Failed to reset password';
        setError(errorMessage);
        
        // Handle specific error messages
        if (errorMessage.includes('Invalid recovery code')) {
          setFieldErrors(prev => ({ ...prev, recoverySecret: 'Invalid recovery code' }));
        } else if (errorMessage.includes('Too many attempts')) {
          setError('Too many attempts. Try again later.');
        }
      }
    } catch (error) {
      console.error('Reset password error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Check for CORS errors specifically
      if (error.message && (error.message.includes('CORS') || error.message.includes('Network Error'))) {
        console.error('🚫 CORS Error detected:', error.message);
        setError('Connection error. Please check your internet connection and try again.');
        setIsLoading(false);
        return;
      }
      
      // Handle different error statuses
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error || 'Failed to reset password. Please check your recovery code and try again.';
        setError(errorMessage);
        
        // Handle specific error messages from backend
        if (errorMessage.includes('Invalid recovery code') || errorMessage.includes('Invalid recovery')) {
          setFieldErrors(prev => ({ ...prev, recoverySecret: 'Invalid recovery code' }));
        } else if (errorMessage.includes('Too many attempts')) {
          setError('Too many attempts. Try again later.');
        } else if (errorMessage.includes('must be at least')) {
          setFieldErrors(prev => ({ ...prev, newPassword: errorMessage }));
        } else if (errorMessage.includes('Recovery secret is required')) {
          setFieldErrors(prev => ({ ...prev, recoverySecret: 'Recovery secret is required' }));
        } else if (errorMessage.includes('New password is required')) {
          setFieldErrors(prev => ({ ...prev, newPassword: 'New password is required' }));
        }
      } else {
        const errorMessage = error.response?.data?.error || error.message || 'Failed to reset password. Please check your recovery code and try again.';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <motion.div
        className="login-form"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring" }}
      >
        <FaLock className="lock-icon" />
        <h2>Reset Password</h2>
        {success ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ 
              color: '#4facfe', 
              fontSize: '1.1rem', 
              marginBottom: '1rem',
              fontWeight: '600'
            }}>
              ✓ Password successfully updated
            </div>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '0.9rem',
              marginBottom: '1.5rem'
            }}>
              Redirecting to login page...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Hidden username field for accessibility */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value="admin"
              readOnly
              tabIndex={-1}
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                pointerEvents: 'none'
              }}
              aria-hidden="true"
            />
            <div className="password-input-wrapper">
              <input
                type="text"
                placeholder="Secret Recovery Code"
                value={recoverySecret}
                onChange={(e) => {
                  setRecoverySecret(e.target.value);
                  setError('');
                  setFieldErrors(prev => ({ ...prev, recoverySecret: '' }));
                }}
                className={fieldErrors.recoverySecret ? 'password-input-error' : ''}
                required
                autoFocus
                disabled={isLoading}
              />
              {fieldErrors.recoverySecret && (
                <span className="password-error-message">{fieldErrors.recoverySecret}</span>
              )}
            </div>
            <div className="password-input-wrapper" style={{ marginTop: '1rem' }}>
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError('');
                  setFieldErrors(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
                }}
                className={fieldErrors.newPassword ? 'password-input-error' : ''}
                autoComplete="new-password"
                required
                disabled={isLoading}
              />
              {fieldErrors.newPassword && (
                <span className="password-error-message">{fieldErrors.newPassword}</span>
              )}
            </div>
            <div className="password-input-wrapper" style={{ marginTop: '1rem' }}>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                  setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
                }}
                className={fieldErrors.confirmPassword ? 'password-input-error' : ''}
                autoComplete="new-password"
                required
                disabled={isLoading}
              />
              {fieldErrors.confirmPassword && (
                <span className="password-error-message">{fieldErrors.confirmPassword}</span>
              )}
            </div>
            {error && !fieldErrors.recoverySecret && !fieldErrors.newPassword && !fieldErrors.confirmPassword && (
              <span className="password-error-message" style={{ display: 'block', marginTop: '0.5rem' }}>
                {error}
              </span>
            )}
            <button type="submit" disabled={isLoading || success}>
              {isLoading ? 'Resetting...' : 'Set New Password'}
            </button>
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                disabled={isLoading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  textDecoration: 'underline',
                  padding: '0.5rem',
                  opacity: isLoading ? 0.5 : 1
                }}
              >
                Back to Login
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;

